import { useState } from 'react';
import { Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack, TextField, MenuItem, Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ExportMenu } from '../../components/ExportMenu';
import { StatCard } from '../../components/StatCard';
import { EmptyState } from '../../components/EmptyState';
import { integrationLogApi } from '../../api/integration.api';

const n = (x = 0) => Number(x).toLocaleString('en-IN');

export function IntegrationLogsPage() {
  const [status, setStatus] = useState('');
  const [direction, setDirection] = useState('');

  const { data: analytics } = useQuery({ queryKey: ['integration-analytics'], queryFn: () => integrationLogApi.analytics() });
  const { data } = useQuery({
    queryKey: ['integration-logs', status, direction],
    queryFn: () => integrationLogApi.list({ limit: 100, status: status || undefined, direction: direction || undefined }),
  });
  const rows = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="API Audit Logs & Monitoring"
        subtitle="Every inbound and outbound API call, with latency, status and error analytics."
        action={<ExportMenu dataset="integration-logs" permission="integration_log.export" filters={{ status, direction }} />}
      />
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={3}><StatCard color="#1e40af" label="Total Calls" value={n(analytics?.totalCalls)} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#16a34a" label="Success Rate" value={`${analytics?.successRate ?? 100}%`} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#dc2626" label="Errors" value={n(analytics?.errors)} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#d97706" label="Avg Latency" value={`${n(analytics?.avgDurationMs)} ms`} /></Grid>
      </Grid>

      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">All</MenuItem><MenuItem value="success">Success</MenuItem><MenuItem value="error">Error</MenuItem>
            </TextField>
            <TextField select size="small" label="Direction" value={direction} onChange={(e) => setDirection(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">All</MenuItem><MenuItem value="inbound">Inbound</MenuItem><MenuItem value="outbound">Outbound</MenuItem>
            </TextField>
          </Stack>
          {rows.length === 0 ? <EmptyState title="No API calls logged yet" dense /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell><TableCell>Direction</TableCell><TableCell>Event</TableCell>
                  <TableCell>Method</TableCell><TableCell>URL</TableCell><TableCell>HTTP</TableCell>
                  <TableCell>Latency</TableCell><TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((l) => (
                  <TableRow key={l.id} hover>
                    <TableCell>{new Date(l.created_at).toLocaleString()}</TableCell>
                    <TableCell>{l.direction}</TableCell>
                    <TableCell>{l.event ?? '—'}</TableCell>
                    <TableCell>{l.method ?? '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.url ?? '—'}</TableCell>
                    <TableCell>{l.response_status ?? '—'}</TableCell>
                    <TableCell>{l.duration_ms != null ? `${l.duration_ms}ms` : '—'}</TableCell>
                    <TableCell><Chip size="small" color={l.status === 'success' ? 'success' : 'error'} label={l.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
